// React.
import React, { useState, useEffect } from "react";

// Material UI
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

// Components
import AddUserForm from '../users/AddUserForm';

// Redux
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../../../actions/userActions/UserActions";
import { RootStore } from "../../../Store";

// Model Types.
import { UserType } from '../models/ModelTypes';

// Translations.
import { useTranslation } from 'react-i18next';

// Styles.
import useStyles from './AddPreOrderCustomerStyles';

interface AutoCompleteType {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | undefined;
}

interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

interface AddPreOrderCustomerProps {
  selectedCustomer: Customer | null;
  onCustomerChange: (customer: Customer | null) => void;
}

const AddPreOrderCustomer = ({ selectedCustomer, onCustomerChange }: AddPreOrderCustomerProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [t] = useTranslation('global');

  // Redux state.
  const userState = useSelector((state: RootStore) => state.user);
  const { loading, userList } = userState;

  // Local state.
  const [open, setOpen] = useState<boolean>(false);
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<AutoCompleteType | null>(null);
  const [options, setOptions] = useState<AutoCompleteType[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const getUserData = () => {
    const delayDebounceFn = setTimeout(() => {
      if (!loading) {
        const qParams = { searchTerm: autocompleteValue, page: 0, size: 15, paged: true };
        dispatch(getUsers(qParams));
      }
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  };

  // Get the users list when searching
  useEffect(() => {
    if(autocompleteValue.length > 2){
      getUserData();
    }
    // eslint-disable-next-line
  }, [autocompleteValue]);

  // Update options when userList changes
  useEffect(() => {
    if (userList !== undefined) {
      var newUsersList: Array<AutoCompleteType> = [];
      userList?.users.forEach((user: UserType) => {
        let currentUser = {
          email: user.email,
          firstName: user.name,
          lastName: user.lastName,
          phone: user.phone
        };
        newUsersList.push(currentUser);
      });
      setOptions(newUsersList);
    }
  }, [userList]);

  const autocompleteChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setAutocompleteValue(String(event.target.value));
    setSelectedItem(null);
  };

  const handleSelectCustomer = () => {
    if (selectedItem) {
      const newCustomer: Customer = {
        id: `${selectedItem.email}-${Date.now()}`,
        email: selectedItem.email,
        firstName: selectedItem.firstName,
        lastName: selectedItem.lastName,
        phone: selectedItem.phone
      };

      onCustomerChange(newCustomer);
      setSelectedItem(null);
      setAutocompleteValue('');
    }
  };

  const handleRemoveCustomer = () => {
    onCustomerChange(null);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Listen for successful user creation to refresh the search and close modal
  useEffect(() => {
    if (userState.actionStatus?.status === 'success' && modalOpen) {
      setModalOpen(false);
      // Refresh the users search if there's a current search term
      if (autocompleteValue.length > 2) {
        getUserData();
      }
    }
    // eslint-disable-next-line
  }, [userState.actionStatus]);

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" color="primary" className={classes.sectionTitle}>
        {t('preorders.select-customer')}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <Autocomplete
            id="preorder-customers-search"
            className={classes.autocomplete}
            options={options}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={selectedItem}
            onChange={(event: any, newValue: AutoCompleteType | null) => {
              setSelectedItem(newValue);
            }}
            getOptionSelected={(option, value) => option.email === value.email}
            getOptionLabel={(option) => 
              `${option.firstName} ${option.lastName} | ${option.email}${option.phone ? ` | ${option.phone}` : ''}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('users.user-search') || 'Search customers'}
                value={autocompleteValue}
                onChange={autocompleteChange}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <div className={classes.buttonContainer}>
            <Button 
              onClick={handleSelectCustomer}
              variant="contained" 
              color="primary"
              disabled={!selectedItem}
              className={classes.addButton}
              fullWidth
            >
              {t('preorders.select-customer')}
            </Button>
            <Button 
              onClick={handleOpenModal}
              variant="outlined" 
              color="primary"
              className={classes.newUserButton}
              fullWidth
            >
              {t('preorders.new-customer')}
            </Button>
          </div>
        </Grid>
      </Grid>

      {selectedCustomer && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div className={classes.selectedCustomerContainer}>
            <Typography variant="subtitle1" gutterBottom>
              {t('preorders.selected-customer') || 'Selected Customer'}:
            </Typography>
            <div>
              <Typography>{t('preorders.selected-customer-name')}: {selectedCustomer.firstName}, {selectedCustomer.lastName}</Typography>
              <Typography>{t('preorders.selected-customer-email')}: {selectedCustomer.email}</Typography>
              <Typography>{t('preorders.selected-customer-phone')}: {selectedCustomer.phone || 'No phone'}</Typography>
            </div>
            <Button onClick={handleRemoveCustomer} color="secondary">
              {t('preorders.remove-customer') || 'Remove Customer'}
            </Button>
          </div>
        </>
      )}

      {/* Modal for adding new user */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('preorders.add-new-customer') || 'Add New Customer'}
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <AddUserForm defaultRole="ROLE_USER" hideRoleSelector={true} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t('preorders.cancel') || 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AddPreOrderCustomer;